// apps/client/src/context/CompanyContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';

interface Company {
  id: string;
  companyName: string;
  [key: string]: any;
}

interface CompanyContextType {
  company: Company | null;
  loading: boolean;
  error: string | null;
  updateCompanyName: (newName: string) => Promise<void>;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) throw new Error('useCompany must be used within CompanyProvider');
  return context;
}

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }: any) => {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists() || !userDocSnap.data().companyId) {
          setError('User is not associated with a company.');
          setLoading(false);
          return;
        }
        
        const companyId = userDocSnap.data().companyId;

        const companyDocRef = doc(db, 'companies', companyId);
        const companyDocSnap = await getDoc(companyDocRef);

        if (companyDocSnap.exists()) {
          setCompany({ id: companyDocSnap.id, ...companyDocSnap.data() } as Company);
        } else {
          setError(`Company data not found for ID: ${companyId}`);
        }
      } catch (err: any) {
        setError('Failed to load company data.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [user]);

  const updateCompanyName = async (newName: string) => {
    if (!company) return;
    const companyDocRef = doc(db, 'companies', company.id);
    await updateDoc(companyDocRef, {
      companyName: newName
    });
    setCompany(prev => prev ? { ...prev, companyName: newName } : null);
  };

  const value: CompanyContextType = {
    company,
    loading,
    error,
    updateCompanyName
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};